import { Fragment, useState } from 'react';
import { formatDate } from '../lib/format.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Collapse,
  Tooltip,
  Dialog,
  FormControlLabel,
  Switch,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { OnboardingCredentials } from '../components/OnboardingCredentials.js';
import { SdkInstall } from '../components/SdkInstall.js';
import { useMe } from '../api/hooks.js';
import { api, actAsStore } from '../api/client.js';
import LoginIcon from '@mui/icons-material/Login';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

interface Plan {
  code: string;
  name: string;
}

interface OrgUser {
  id: string;
  email: string;
  fullName: string | null;
  role: string;
  status: string;
  lastLoginAt: string | null;
  loginCount: number;
  onboarded: boolean;
}

interface OnboardingDetails {
  organizationName: string;
  email: string | null;
  complete: boolean;
  projectId: string | null;
  projectName: string | null;
  publicKey: string | null;
  apiKey: string | null;
  setupLink?: string;
  setupLinkExpiresAt?: string;
}

interface Org {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string | null;
  planCode: string;
  planName: string;
  status: string;
  addons: Addons | null;
  seats: number | null;
  members: number;
  onboardingComplete: boolean;
  projects: number;
  sessions: number;
  logins: number;
  users: OrgUser[];
  currentPeriodEnd: string | null;
  createdAt: string;
}

interface Addons {
  sessions: number;
  projects: number;
  integrations: number;
  coverOps: boolean;
}

const fmtDate = (d: string | null) => (d ? formatDate(d) : '—');

export function AdminCompaniesPage() {
  const all = useT();
  const t = all.admin.companies;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const common = all.common;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detailsFor, setDetailsFor] = useState<Org | null>(null);
  const [addonsFor, setAddonsFor] = useState<Org | null>(null);
  const [addonForm, setAddonForm] = useState<Addons>({ sessions: 0, projects: 0, integrations: 0, coverOps: false });
  const openAddons = (o: Org) => {
    setAddonForm(o.addons ?? { sessions: 0, projects: 0, integrations: 0, coverOps: false });
    setAddonsFor(o);
  };

  // Loaded on demand: the ingest key and the set-password link are only
  // fetched when an admin actually opens a company's hand-off sheet.
  const details = useQuery({
    enabled: !!detailsFor,
    queryKey: ['admin-onboarding', detailsFor?.id],
    queryFn: async () =>
      (await api.get(`/v1/admin/organizations/${detailsFor!.id}/onboarding`))
        .data as OnboardingDetails,
  });

  const orgs = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-orgs'],
    queryFn: async () => (await api.get('/v1/admin/organizations')).data.data as Org[],
  });

  const sort = useSort<
    'company' | 'owner' | 'members' | 'logins' | 'sessions' | 'onboarding' | 'status' | 'subscription' | 'renews' | 'joined'
  >('joined');
  const rows = sort.sorted(orgs.data ?? [], {
    company: (o) => o.name,
    owner: (o) => o.ownerEmail,
    members: (o) => o.members,
    logins: (o) => o.logins,
    sessions: (o) => o.sessions,
    onboarding: (o) => (o.onboardingComplete ? 1 : 0),
    status: (o) => o.status,
    subscription: (o) => o.planName,
    renews: (o) => o.currentPeriodEnd,
    joined: (o) => o.createdAt,
  });

  const plans = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-plans'],
    queryFn: async () => (await api.get('/v1/admin/plans')).data.data as Plan[],
  });

  const setOrgPlan = useMutation({
    mutationFn: async (v: { orgId: string; planCode: string }) =>
      api.put(`/v1/admin/organizations/${v.orgId}/plan`, { planCode: v.planCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-orgs'] });
      setToast('Subscription updated');
    },
  });

  const saveAddons = useMutation({
    mutationFn: async (v: { orgId: string; addons: Addons }) =>
      api.put(`/v1/admin/organizations/${v.orgId}/addons`, v.addons),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-orgs'] });
      setAddonsFor(null);
      setToast(t.addons.saved);
    },
  });

  if (me && !me.isSuperAdmin) {
    return <Alert severity="error">{needSuperAdmin}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ApartmentIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 44 }} />
              <TableCell>{t.columns.company}</TableCell>
              <TableCell>{t.columns.owner}</TableCell>
              <TableCell align="center">{t.columns.members}</TableCell>
              <TableCell align="center">{t.columns.logins}</TableCell>
              <TableCell align="center">{t.columns.sessions}</TableCell>
              <TableCell>{t.columns.onboarding}</TableCell>
              <TableCell>{t.columns.status}</TableCell>
              <TableCell>{t.columns.subscription}</TableCell>
              <TableCell>{t.columns.renews}</TableCell>
              <TableCell>{t.columns.joined}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((o) => (
              <Fragment key={o.id}>
              <TableRow hover>
                <TableCell sx={{ pr: 0 }}>
                  <IconButton
                    size="small"
                    aria-label={expanded === o.id ? 'Hide users' : 'Show users'}
                    onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  >
                    {expanded === o.id ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {o.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {o.slug}
                      </Typography>
                    </Stack>
                    <Tooltip title={t.actAs}>
                      <IconButton
                        size="small"
                        aria-label={`Open as ${o.name}`}
                        onClick={() => {
                          actAsStore.set(o.id);
                          window.location.href = '/dashboard';
                        }}
                      >
                        <LoginIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {o.ownerEmail ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">{o.members}</TableCell>
                <TableCell align="center">
                  <Tooltip title={t.loginsHint}>
                    <span>{o.logins}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={`Recorded sessions across ${o.projects} project(s)`}>
                    <span>{o.sessions.toLocaleString()}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      o.onboardingComplete
                        ? 'The owner has set their password and can sign in'
                        : 'Waiting for the owner to set their password. Click to re-open the details'
                    }
                  >
                    <Chip
                      size="small"
                      clickable
                      onClick={() => setDetailsFor(o)}
                      label={o.onboardingComplete ? 'complete' : 'pending'}
                      color={o.onboardingComplete ? 'success' : 'warning'}
                      variant={o.onboardingComplete ? 'outlined' : 'filled'}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={o.status === 'none' ? 'default' : o.status}
                    color={o.status === 'active' ? 'success' : o.status === 'past_due' ? 'warning' : 'default'}
                    variant={o.status === 'active' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={o.planCode}
                    onChange={(e) => setOrgPlan.mutate({ orgId: o.id, planCode: e.target.value })}
                    sx={{ width: 150 }}
                  >
                    {(plans.data ?? []).map((p) => (
                      <MenuItem key={p.code} value={p.code}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Button size="small" onClick={() => openAddons(o)} sx={{ minWidth: 0, px: 0.5 }}>
                      {t.addons.button}
                    </Button>
                    {o.addons && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {t.addons.summary(
                          o.addons.sessions.toLocaleString(),
                          o.addons.projects,
                          o.addons.integrations,
                          o.addons.coverOps,
                        )}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {fmtDate(o.currentPeriodEnd)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {fmtDate(o.createdAt)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ py: 0, border: 0 }} colSpan={10}>
                  <Collapse in={expanded === o.id} timeout="auto" unmountOnExit>
                    <Box sx={{ py: 2, pl: 5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        People in {o.name}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t.memberColumns.user}</TableCell>
                            <TableCell>{t.memberColumns.role}</TableCell>
                            <TableCell>{t.memberColumns.status}</TableCell>
                            <TableCell align="center">{t.memberColumns.logins}</TableCell>
                            <TableCell>{t.memberColumns.lastLogin}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {o.users.map((u) => (
                            <TableRow key={u.id}>
                              <TableCell>
                                <Stack>
                                  <Typography variant="body2">{u.email}</Typography>
                                  {u.fullName && (
                                    <Typography variant="caption" color="text.secondary">
                                      {u.fullName}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>{u.role}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={u.onboarded ? 'onboarded' : u.status.toLowerCase()}
                                  color={
                                    u.status === 'ACTIVE'
                                      ? 'success'
                                      : u.status === 'INVITED'
                                        ? 'warning'
                                        : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell align="center">{u.loginCount}</TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {u.lastLoginAt ? fmtDate(u.lastLoginAt) : 'never'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
              </Fragment>
            ))}
            {orgs.data && orgs.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No companies yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={!!detailsFor}
        onClose={() => setDetailsFor(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Onboarding · {detailsFor?.name}</DialogTitle>
        <DialogContent dividers>
          {details.isPending && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {details.data && (
            <>
              <Alert
                severity={details.data.complete ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              >
                {details.data.complete ? (
                  <>
                    {details.data.email} has set their password. Onboarding is finished. The
                    credentials below are what they see under API Keys.
                  </>
                ) : (
                  <>
                    {details.data.email} has not set a password yet, so nobody can sign in to this
                    company. Send them the link below. It is the only way into the account
                    {details.data.setupLinkExpiresAt
                      ? ` and expires on ${formatDate(details.data.setupLinkExpiresAt)}.`
                      : '.'}
                  </>
                )}
              </Alert>

              <OnboardingCredentials data={details.data} />

              {details.data.projectId && details.data.apiKey && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SdkInstall projectId={details.data.projectId} apiKey={details.data.apiKey} />
                </>
              )}
            </>
          )}
          {details.isError && (
            <Alert severity="error">{t.detailsFailed}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsFor(null)}>{common.close}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!addonsFor} onClose={() => setAddonsFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{addonsFor ? t.addons.title(addonsFor.name) : ''}</DialogTitle>
        <DialogContent dividers>
          {addonsFor && !addonsFor.addons ? (
            <Alert severity="info">{t.addons.onFree}</Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t.addons.note}
              </Typography>
              {(['sessions', 'projects', 'integrations'] as const).map((k) => (
                <TextField
                  key={k}
                  label={t.addons[k]}
                  size="small"
                  type="number"
                  value={addonForm[k]}
                  onChange={(e) => setAddonForm((f) => ({ ...f, [k]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
                  inputProps={{ min: 0, step: k === 'sessions' ? 1000 : 1 }}
                />
              ))}
              <FormControlLabel
                control={
                  <Switch
                    checked={addonForm.coverOps}
                    onChange={(e) => setAddonForm((f) => ({ ...f, coverOps: e.target.checked }))}
                  />
                }
                label={t.addons.coverOps}
              />
              {saveAddons.isError && (
                <Alert severity="error">
                  {(saveAddons.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
                    ?.message ?? t.detailsFailed}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddonsFor(null)}>{common.close}</Button>
          {addonsFor?.addons && (
            <Button
              variant="contained"
              disabled={saveAddons.isPending}
              onClick={() => saveAddons.mutate({ orgId: addonsFor.id, addons: addonForm })}
            >
              {t.addons.save}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
